// Time in the five AGP ranges as one stacked bar, low to high, with an optional legend.

import type { BandsDTO } from "../../shared/dto.js";
import { cn } from "@/lib/cn";
import { sharePercent } from "@/lib/format";

export const BANDS = [
  { key: "veryLow", label: "Very low", range: "<54", color: "var(--band-very-low)" },
  { key: "low", label: "Low", range: "54–69", color: "var(--band-low)" },
  { key: "inRange", label: "In range", range: "70–180", color: "var(--band-in-range)" },
  { key: "high", label: "High", range: "181–250", color: "var(--band-high)" },
  { key: "veryHigh", label: "Very high", range: ">250", color: "var(--band-very-high)" },
] as const satisfies readonly { key: keyof BandsDTO; label: string; range: string; color: string }[];

export function RangeBar({ bands, className }: { bands: BandsDTO | null; className?: string }) {
  return (
    <div
      className={cn("flex h-3 w-full overflow-hidden rounded-full bg-surface-muted", className)}
      role="img"
      aria-label={bands ? BANDS.map((b) => `${b.label} ${sharePercent(bands[b.key])}`).join(", ") : "No readings"}
    >
      {bands &&
        BANDS.map((b) =>
          bands[b.key] > 0 ? <div key={b.key} style={{ width: `${bands[b.key]}%`, background: b.color }} title={`${b.label} ${b.range}: ${sharePercent(bands[b.key])}`} /> : null,
        )}
    </div>
  );
}

export function RangeLegend({ bands, className }: { bands: BandsDTO | null; className?: string }) {
  return (
    <ul className={cn("flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted", className)}>
      {BANDS.map((b) => (
        <li key={b.key} className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full" style={{ background: b.color }} aria-hidden />
          <span>
            {b.label} <span className="opacity-75">{b.range}</span>
          </span>
          <span className="font-medium text-fg tabular-nums">{bands ? sharePercent(bands[b.key]) : "–"}</span>
        </li>
      ))}
    </ul>
  );
}
